import { XIcon } from "@/design/icons";
import type { FC } from "react";

import { cn } from "@/agents-v2/utils/cn";

export type ContextChip = {
  key: "selection" | "section" | "tender";
  label: string;
  /** Removable for the next message only. */
  removable?: boolean;
  removed?: boolean;
};

/**
 * What io.ai can see right now, as chips above the composer. Removing one
 * drops it for the next message; the chip comes back after that send.
 */
export const ContextChips: FC<{ chips: ContextChip[]; onToggle: (key: ContextChip["key"]) => void }> = ({
  chips,
  onToggle,
}) => {
  const shown = chips.filter((c) => c.label);
  if (!shown.length) return null;
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-2 pt-1.5 [scrollbar-width:none]">
      {shown.map((chip) => (
        <span
          key={chip.key}
          className={cn(
            "inline-flex h-6 shrink-0 items-center gap-1 rounded-full border border-line pl-2.5 text-xs font-semibold text-fg-2",
            chip.removable ? "pr-1" : "pr-2.5",
            chip.removed && "border-dashed text-fg-4 line-through"
          )}
          title={
            chip.removed
              ? "Your selection will not be sent with the next message"
              : "io.ai will read your selection with the next message"
          }
        >
          {chip.label}
          {chip.removable && (
            <button
              type="button"
              aria-label={chip.removed ? `Include ${chip.label} again` : `Leave ${chip.label} out of the next message`}
              onClick={() => onToggle(chip.key)}
              className="flex size-4 items-center justify-center rounded-full text-fg-3 hover:bg-hover hover:text-fg"
            >
              <XIcon className="size-2.5" />
            </button>
          )}
        </span>
      ))}
    </div>
  );
};
